/**
 * FMB Collection Filter Module
 *
 * Client-side filtering for collection pages powered by the FindMyMarble API.
 * Fetches stone type data and facets, builds a dynamic filter sidebar, and
 * shows/hides Shopify-rendered product cards based on active selections.
 *
 * Dependencies:
 *   - window.FindMyMarbleAPI (getCollectionData method)
 *   - assets/section-fmb-collection-filters.css (styling)
 *   - snippets/fmb-collection-filters.liquid (DOM shell)
 *
 * Filter logic: AND between groups, OR within groups.
 * Graceful degradation: if API fails, products remain visible without filters.
 */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
   * Constants
   * ----------------------------------------------------------------------- */

  var SELECTORS = {
    SIDEBAR: '[data-fmb-filters]',
    LOADING: '[data-fmb-loading]',
    CONTENT: '[data-fmb-content]',
    ACTIVE_FILTERS: '[data-fmb-active-filters]',
    FILTER_COUNT: '[data-fmb-filter-count]',
    MOBILE_TRIGGER: '[data-fmb-mobile-trigger]',
    MOBILE_DRAWER: '[data-fmb-mobile-drawer]',
    MOBILE_CLOSE: '[data-fmb-mobile-close]',
    MOBILE_APPLY: '[data-fmb-mobile-apply]',
    CLEAR_ALL: '[data-fmb-clear-all]',
    NO_RESULTS: '[data-fmb-no-results]',
    MOBILE_COUNT: '[data-fmb-mobile-count]',
    PRODUCT_CARD: '.collection-product-card',
    PRODUCT_GRID: '#product-grid',
    CARD_BADGE: '.card__badge'
  };

  var CLASSES = {
    HIDDEN: 'fmb-hidden',
    FILTER_GROUP: 'fmb-filter-group',
    FILTER_SUMMARY: 'fmb-filter-group__summary',
    FILTER_LIST: 'fmb-filter-group__list',
    FILTER_ITEM: 'fmb-filter-group__item',
    FILTER_CHECKBOX: 'fmb-filter-checkbox',
    FILTER_COUNT: 'fmb-filter-count',
    ACTIVE_FILTER: 'fmb-active-filter',
    ACTIVE_CLOSE: 'fmb-active-filter__close',
    STOCK_BADGE: 'fmb-stock-badge'
  };

  var FACET_LABELS = {
    color_tone: 'Colour Tone',
    pattern_type: 'Pattern',
    visual_impact: 'Visual Impact',
    finish: 'Finish',
    thickness_mm: 'Thickness (mm)',
    stone_category: 'Stone Type',
    origin_country: 'Origin',
    quality_grade: 'Quality Grade'
  };

  var FACET_FIELD_MAP = {
    color_tone: { prop: 'color_tones', isArray: true },
    pattern_type: { prop: 'pattern_types', isArray: true },
    visual_impact: { prop: 'visual_impacts', isArray: true },
    finish: { prop: 'finishes', isArray: true },
    thickness_mm: { prop: 'thicknesses', isArray: true },
    stone_category: { prop: 'stone_category', isArray: false },
    origin_country: { prop: 'origin_countries', isArray: true },
    quality_grade: { prop: 'quality_grades', isArray: true }
  };

  var NUMERIC_FACETS = { thickness_mm: true };

  var ORDERED_FACETS = {
    visual_impact: ['very_low', 'low', 'moderate', 'high', 'very_high']
  };

  var URL_PREFIX = 'fmb_';
  var PAGE_SIZE = 24;

  /* -----------------------------------------------------------------------
   * HTML Helpers
   * ----------------------------------------------------------------------- */

  /**
   * Escapes HTML special characters to prevent XSS.
   * @param {string} str - Raw string
   * @returns {string} Escaped string safe for innerHTML
   */
  function escapeHtml(str) {
    if (!str && str !== 0) return '';
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(String(str)));
    return div.innerHTML;
  }

  /**
   * Normalizes a stone name for case-insensitive matching.
   * @param {string} name - Stone name
   * @returns {string} Lowercased trimmed name
   */
  function normalizeStoneName(name) {
    if (!name) return '';
    return name.trim().toLowerCase();
  }

  /**
   * Formats a facet value for display (capitalize first letter).
   * @param {string|number} value - Raw facet value
   * @returns {string} Display-ready value
   */
  function formatFacetValue(value) {
    if (typeof value === 'number') return String(value);
    if (!value) return '';
    return value.replace(/_/g, ' ').replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  /* -----------------------------------------------------------------------
   * FMBCollectionFilter
   * ----------------------------------------------------------------------- */

  /**
   * Core filter controller for FMB-powered collection pages.
   * @constructor
   */
  function FMBCollectionFilter() {
    this.sidebar = null;
    this.products = [];
    this.filteredProducts = [];
    this.currentPage = 1;
    this.stoneTypes = {};
    this.facets = {};
    this.activeFilters = {};
    this._allCardElements = null;
  }

  /* -- init --------------------------------------------------------------- */

  /** Orchestrates the full filter lifecycle. */
  FMBCollectionFilter.prototype.init = async function () {
    this.sidebar = document.querySelector(SELECTORS.SIDEBAR);
    if (!this.sidebar) return;

    /* Wait for FindMyMarbleAPI if not yet available (script load order) */
    if (!window.FindMyMarbleAPI) {
      await new Promise(function (resolve) {
        var attempts = 0;
        var poll = setInterval(function () {
          attempts++;
          if (window.FindMyMarbleAPI || attempts > 50) {
            clearInterval(poll);
            resolve();
          }
        }, 100);
      });
    }
    if (!window.FindMyMarbleAPI) {
      this._showGracefulFallback();
      return;
    }

    try {
      await this._loadAllPages();
      this.extractProducts();
      await this.fetchData();
      this.mergeData();
      this.renderFilterSidebar();
      this.enhanceCards();
      this.loadURLState(); /* calls _renderCurrentPage via applyFilters if URL has filter params */
      if (!this._hasActiveFilters()) {
        this._renderCurrentPage(); /* render page 1 with all products when no URL filters */
      }
      this.bindEvents();
    } catch (err) {
      console.warn('FMBCollectionFilter: initialization failed —', err.message);
      this._showGracefulFallback();
    }
  };

  /**
   * On failure, hides the loading skeleton and keeps products visible.
   * @private
   */
  FMBCollectionFilter.prototype._showGracefulFallback = function () {
    var loading = document.querySelector(SELECTORS.LOADING);
    if (loading) loading.setAttribute('hidden', '');

    var sidebar = this.sidebar;
    if (sidebar) sidebar.setAttribute('hidden', '');
  };

  /* -- loadAllPages ------------------------------------------------------- */

  /**
   * Fetches all paginated pages and merges their product cards into the DOM.
   * This ensures client-side filters work across the entire collection,
   * not just the current page. Hides pagination after loading.
   * @private
   */
  FMBCollectionFilter.prototype._loadAllPages = async function () {
    var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
    if (!grid) return;

    var paginationEl = document.querySelector('.pagination');

    /* Single page — capture current DOM cards and return early */
    if (!paginationEl) {
      var single = grid.querySelectorAll(SELECTORS.PRODUCT_CARD);
      this._allCardElements = Array.prototype.slice.call(single);
      return;
    }

    var totalPages = this._detectTotalPages(paginationEl);
    if (totalPages <= 1) {
      var single2 = grid.querySelectorAll(SELECTORS.PRODUCT_CARD);
      this._allCardElements = Array.prototype.slice.call(single2);
      return;
    }

    /* Determine which page we're on (default 1) */
    var params = new URLSearchParams(window.location.search);
    var currentPage = parseInt(params.get('page'), 10) || 1;

    /* Capture current page's cards from DOM before any fetching */
    var currentPageCards = Array.prototype.slice.call(
      grid.querySelectorAll(SELECTORS.PRODUCT_CARD)
    );

    /* Build list of remote pages to fetch (all except current) */
    var pagesToFetch = [];
    for (var i = 1; i <= totalPages; i++) {
      if (i !== currentPage) pagesToFetch.push(i);
    }

    /* Fetch all other pages concurrently */
    var basePath = window.location.pathname;
    var fetches = pagesToFetch.map(function (page) {
      var url = basePath + '?page=' + page;
      return fetch(url)
        .then(function (res) { return res.text(); })
        .then(function (html) { return { page: page, html: html }; })
        .catch(function () { return null; });
    });

    var results = await Promise.all(fetches);
    results = results.filter(Boolean);

    /* Combine current page + fetched pages, sorted by page number */
    var allResults = results.concat([{ page: currentPage, isCurrent: true }]);
    allResults.sort(function (a, b) { return a.page - b.page; });

    /* Build ordered card array — store in memory, do NOT inject into DOM */
    var allCards = [];
    for (var j = 0; j < allResults.length; j++) {
      if (allResults[j].isCurrent) {
        for (var k = 0; k < currentPageCards.length; k++) {
          allCards.push(currentPageCards[k]);
        }
      } else {
        var parsed = this._extractCardsFromHtml(allResults[j].html);
        for (var m = 0; m < parsed.length; m++) {
          allCards.push(parsed[m]);
        }
      }
    }

    this._allCardElements = allCards;

    /* Hide native pagination — virtual pagination takes over */
    this._hidePagination();
  };

  /**
   * Detects total page count from pagination links.
   * @param {HTMLElement} paginationEl - The pagination nav element
   * @returns {number} Total number of pages
   * @private
   */
  FMBCollectionFilter.prototype._detectTotalPages = function (paginationEl) {
    var links = paginationEl.querySelectorAll('.pagination__item, .pagination__item--current');
    var max = 1;

    for (var i = 0; i < links.length; i++) {
      var text = links[i].textContent.trim();
      var num = parseInt(text, 10);
      if (!isNaN(num) && num > max) max = num;
    }

    return max;
  };

  /**
   * Parses an HTML string and extracts product card li elements.
   * @param {string} html - Full page HTML
   * @returns {Array<HTMLElement>} Array of product card elements
   * @private
   */
  FMBCollectionFilter.prototype._extractCardsFromHtml = function (html) {
    var parser = new DOMParser();
    var doc = parser.parseFromString(html, 'text/html');
    var cards = doc.querySelectorAll(SELECTORS.PRODUCT_CARD);
    var result = [];

    for (var i = 0; i < cards.length; i++) {
      result.push(document.importNode(cards[i], true));
    }

    return result;
  };

  /**
   * Hides all native pagination and load-more UI elements.
   * @private
   */
  FMBCollectionFilter.prototype._hidePagination = function () {
    var selectors = [
      '.pagination-wrapper',
      '.pagination',
      '.load-more',
      '.infinite-scroll'
    ];

    for (var i = 0; i < selectors.length; i++) {
      var els = document.querySelectorAll(selectors[i]);
      for (var j = 0; j < els.length; j++) {
        els[j].style.display = 'none';
      }
    }
  };

  /**
   * Renders the current virtual page of products into the DOM.
   * Only PAGE_SIZE cards are ever in the DOM at once.
   * @private
   */
  FMBCollectionFilter.prototype._renderCurrentPage = function () {
    var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
    if (!grid) return;

    var start = (this.currentPage - 1) * PAGE_SIZE;
    var end = start + PAGE_SIZE;
    var pageProducts = this.filteredProducts.slice(start, end);

    /* Remove only product cards from grid (preserve no-results message etc.) */
    var existing = grid.querySelectorAll(SELECTORS.PRODUCT_CARD);
    for (var i = 0; i < existing.length; i++) {
      existing[i].parentNode.removeChild(existing[i]);
    }

    for (var j = 0; j < pageProducts.length; j++) {
      grid.appendChild(pageProducts[j].element);
    }

    this._renderPagination();
  };

  /**
   * Injects or updates the virtual pagination controls below the product grid.
   * @private
   */
  FMBCollectionFilter.prototype._renderPagination = function () {
    var totalPages = Math.ceil(this.filteredProducts.length / PAGE_SIZE);
    var existing = document.querySelector('.fmb-virtual-pagination');

    if (totalPages <= 1) {
      if (existing) existing.parentNode.removeChild(existing);
      return;
    }

    var prevDisabled = this.currentPage === 1 ? ' disabled' : '';
    var nextDisabled = this.currentPage === totalPages ? ' disabled' : '';
    var html =
      '<div class="fmb-virtual-pagination">' +
        '<button class="fmb-page-btn" data-fmb-page="prev"' + prevDisabled + '>&#8592; Previous</button>' +
        '<span class="fmb-page-info">Page ' + this.currentPage + ' of ' + totalPages + '</span>' +
        '<button class="fmb-page-btn" data-fmb-page="next"' + nextDisabled + '>Next &#8594;</button>' +
      '</div>';

    if (existing) {
      existing.outerHTML = html;
    } else {
      var grid = document.querySelector(SELECTORS.PRODUCT_GRID);
      if (grid && grid.parentNode) {
        grid.insertAdjacentHTML('afterend', html);
      }
    }
  };

  /* -- extractProducts ---------------------------------------------------- */

  /** Reads product cards from the in-memory array into this.products. */
  FMBCollectionFilter.prototype.extractProducts = function () {
    var source = this._allCardElements ||
      Array.prototype.slice.call(document.querySelectorAll(SELECTORS.PRODUCT_CARD));

    var products = [];
    for (var i = 0; i < source.length; i++) {
      products.push({
        element: source[i],
        stoneName: source[i].getAttribute('data-stone-name') || '',
        fmb: null,
        visible: true
      });
    }

    this.products = products;
    this.filteredProducts = products.slice();
  };

  /* -- fetchData ---------------------------------------------------------- */

  /** Fetches collection data from the FMB API and indexes stone types. */
  FMBCollectionFilter.prototype.fetchData = async function () {
    var result = await window.FindMyMarbleAPI.getCollectionData();

    if (result.error) {
      throw new Error(result.message || 'API returned an error');
    }

    this.facets = result.facets || {};
    this._indexStoneTypes(result.stone_types || []);
  };

  /**
   * Indexes stone types by normalized name for O(1) lookup.
   * @param {Array} stoneTypes - Array of stone type objects from API
   * @private
   */
  FMBCollectionFilter.prototype._indexStoneTypes = function (stoneTypes) {
    var index = {};

    for (var i = 0; i < stoneTypes.length; i++) {
      var key = normalizeStoneName(stoneTypes[i].stone_name);
      if (key) index[key] = stoneTypes[i];
    }

    this.stoneTypes = index;
  };

  /* -- mergeData ---------------------------------------------------------- */

  /** Matches each product card to its FMB stone type data. */
  FMBCollectionFilter.prototype.mergeData = function () {
    for (var i = 0; i < this.products.length; i++) {
      var key = normalizeStoneName(this.products[i].stoneName);
      if (key && this.stoneTypes[key]) {
        this.products[i].fmb = this.stoneTypes[key];
      }
    }
  };

  /* -- renderFilterSidebar ------------------------------------------------ */

  /** Builds the filter sidebar UI dynamically from API facets. */
  FMBCollectionFilter.prototype.renderFilterSidebar = function () {
    var contentEl = document.querySelector(SELECTORS.CONTENT);
    var loadingEl = document.querySelector(SELECTORS.LOADING);
    if (!contentEl) return;

    var html = this._buildFilterGroupsHtml();

    contentEl.innerHTML = html;
    contentEl.removeAttribute('hidden');

    if (loadingEl) loadingEl.setAttribute('hidden', '');
  };

  /**
   * Generates HTML for all filter groups that have values.
   * @returns {string} Combined HTML for all filter groups
   * @private
   */
  FMBCollectionFilter.prototype._buildFilterGroupsHtml = function () {
    var groups = [];
    var facetKeys = Object.keys(FACET_LABELS);

    for (var i = 0; i < facetKeys.length; i++) {
      var key = facetKeys[i];
      var values = this.facets[key];

      if (!values || values.length === 0) continue;
      groups.push(this._buildSingleGroupHtml(key, values));
    }

    return groups.join('');
  };

  /**
   * Generates HTML for a single filter group.
   * @param {string} facetKey - Facet key (e.g. 'stone_category')
   * @param {Array} values - Array of {value, count} objects
   * @returns {string} HTML for one details/summary filter group
   * @private
   */
  FMBCollectionFilter.prototype._buildSingleGroupHtml = function (facetKey, values) {
    var label = FACET_LABELS[facetKey] || facetKey;
    var sorted = values.slice();
    if (NUMERIC_FACETS[facetKey]) {
      sorted.sort(function (a, b) { return Number(a.value) - Number(b.value); });
    } else if (ORDERED_FACETS[facetKey]) {
      var order = ORDERED_FACETS[facetKey];
      sorted.sort(function (a, b) { return order.indexOf(a.value) - order.indexOf(b.value); });
    }
    var items = this._buildFilterItemsHtml(facetKey, sorted);

    return (
      '<details class="' + CLASSES.FILTER_GROUP + '" open data-facet-key="' + escapeHtml(facetKey) + '">' +
        '<summary class="' + CLASSES.FILTER_SUMMARY + '">' + escapeHtml(label) + '</summary>' +
        '<ul class="' + CLASSES.FILTER_LIST + '">' + items + '</ul>' +
      '</details>'
    );
  };

  /**
   * Generates HTML for the checkbox items within a filter group.
   * @param {string} facetKey - Facet key
   * @param {Array} values - Array of {value, count} objects
   * @returns {string} HTML for all li items
   * @private
   */
  FMBCollectionFilter.prototype._buildFilterItemsHtml = function (facetKey, values) {
    var items = [];

    for (var i = 0; i < values.length; i++) {
      var val = values[i].value;
      var count = values[i].count;
      var inputId = 'fmb-' + facetKey + '-' + i;

      items.push(
        '<li class="' + CLASSES.FILTER_ITEM + '">' +
          '<input type="checkbox" id="' + escapeHtml(inputId) + '"' +
            ' value="' + escapeHtml(val) + '"' +
            ' data-facet="' + escapeHtml(facetKey) + '">' +
          '<label class="' + CLASSES.FILTER_CHECKBOX + '" for="' + escapeHtml(inputId) + '">' +
            escapeHtml(formatFacetValue(val)) +
            ' <span class="' + CLASSES.FILTER_COUNT + '" data-facet="' + escapeHtml(facetKey) + '" data-value="' + escapeHtml(val) + '">(' + count + ')</span>' +
          '</label>' +
        '</li>'
      );
    }

    return items.join('');
  };

  /* -- applyFilters ------------------------------------------------------- */

  /** Reads active checkboxes, computes filtered set, re-renders page 1. */
  FMBCollectionFilter.prototype.applyFilters = function () {
    this._readActiveFilters();

    var hasActiveFilters = this._hasActiveFilters();
    var filtered = [];

    for (var i = 0; i < this.products.length; i++) {
      var visible = this._isProductVisible(this.products[i], hasActiveFilters);
      this.products[i].visible = visible;
      if (visible) filtered.push(this.products[i]);
    }

    this.filteredProducts = filtered;
    this.currentPage = 1;
    this._renderCurrentPage();

    this.updateCounts();
    this.updateActiveFilters();
    this.updateURL();
  };

  /**
   * Reads all checked checkboxes into this.activeFilters.
   * @private
   */
  FMBCollectionFilter.prototype._readActiveFilters = function () {
    var filters = {};
    var checkboxes = this.sidebar.querySelectorAll('input[type="checkbox"]:checked');

    for (var i = 0; i < checkboxes.length; i++) {
      var facet = checkboxes[i].getAttribute('data-facet');
      var value = checkboxes[i].value;

      if (!filters[facet]) filters[facet] = [];
      filters[facet].push(value);
    }

    this.activeFilters = filters;
  };

  /**
   * Checks whether any filters are currently active.
   * @returns {boolean}
   * @private
   */
  FMBCollectionFilter.prototype._hasActiveFilters = function () {
    var keys = Object.keys(this.activeFilters);
    return keys.length > 0;
  };

  /**
   * Determines whether a single product should be visible.
   * @param {Object} product - Product object from this.products
   * @param {boolean} hasActiveFilters - Whether any filters are active
   * @returns {boolean} true if product should be shown
   * @private
   */
  FMBCollectionFilter.prototype._isProductVisible = function (product, hasActiveFilters) {
    if (!hasActiveFilters) return true;
    if (!product.fmb) return false;

    var facetKeys = Object.keys(this.activeFilters);

    for (var i = 0; i < facetKeys.length; i++) {
      var matched = this._matchesFacetGroup(product.fmb, facetKeys[i]);
      if (!matched) return false;
    }

    return true;
  };

  /**
   * Checks if a product matches any selected value in one facet group (OR logic).
   * @param {Object} fmbData - Stone type data from API
   * @param {string} facetKey - Facet key
   * @returns {boolean} true if product matches at least one selected value
   * @private
   */
  FMBCollectionFilter.prototype._matchesFacetGroup = function (fmbData, facetKey) {
    var mapping = FACET_FIELD_MAP[facetKey];
    if (!mapping) return true;

    var selectedValues = this.activeFilters[facetKey];
    var productValue = fmbData[mapping.prop];

    if (productValue === undefined || productValue === null) return false;

    if (mapping.isArray) {
      return this._matchesArrayField(productValue, selectedValues);
    }
    return this._matchesScalarField(productValue, selectedValues);
  };

  /**
   * OR match: checks if any value in productValues exists in selectedValues.
   * @param {Array} productValues - Product's array field (e.g. origin_countries)
   * @param {Array} selectedValues - User's selected filter values
   * @returns {boolean}
   * @private
   */
  FMBCollectionFilter.prototype._matchesArrayField = function (productValues, selectedValues) {
    if (!Array.isArray(productValues)) return false;

    for (var i = 0; i < productValues.length; i++) {
      if (selectedValues.indexOf(String(productValues[i])) !== -1) return true;
    }

    return false;
  };

  /**
   * Checks if a scalar value exists in selectedValues.
   * @param {*} productValue - Product's scalar field value
   * @param {Array} selectedValues - User's selected filter values
   * @returns {boolean}
   * @private
   */
  FMBCollectionFilter.prototype._matchesScalarField = function (productValue, selectedValues) {
    return selectedValues.indexOf(String(productValue)) !== -1;
  };

  /* -- updateCounts ------------------------------------------------------- */

  /** Updates visible product count and per-filter-option counts. */
  FMBCollectionFilter.prototype.updateCounts = function () {
    var visibleCount = this._countVisibleProducts();
    this._updateProductCountDisplay(visibleCount);
    this._updatePerFilterCounts();
    this._toggleNoResults(visibleCount === 0 && this._hasActiveFilters());
  };

  /**
   * Returns the count of products passing the current filters.
   * @returns {number}
   * @private
   */
  FMBCollectionFilter.prototype._countVisibleProducts = function () {
    return this.filteredProducts.length;
  };

  /**
   * Updates all [data-fmb-filter-count] elements with the visible count.
   * @param {number} count - Number of visible products
   * @private
   */
  FMBCollectionFilter.prototype._updateProductCountDisplay = function (count) {
    var els = document.querySelectorAll(SELECTORS.FILTER_COUNT);
    for (var i = 0; i < els.length; i++) {
      els[i].textContent = count;
    }
  };

  /**
   * Recalculates how many visible products match each filter option.
   * When no filters are active, restores the original API facet counts
   * (which cover all stone types, not just DOM-matched products).
   * @private
   */
  FMBCollectionFilter.prototype._updatePerFilterCounts = function () {
    var badges = this.sidebar.querySelectorAll('.' + CLASSES.FILTER_COUNT);
    var hasActive = this._hasActiveFilters();

    for (var i = 0; i < badges.length; i++) {
      var facetKey = badges[i].getAttribute('data-facet');
      var value = badges[i].getAttribute('data-value');
      var count;

      if (!hasActive) {
        count = this._getOriginalFacetCount(facetKey, value);
      } else {
        count = this._countMatchesForValue(facetKey, value);
      }

      badges[i].textContent = '(' + count + ')';
    }
  };

  /**
   * Looks up the original count for a facet value from the API response.
   * @param {string} facetKey - Facet key
   * @param {string} value - Facet value
   * @returns {number} Original count from API, or 0 if not found
   * @private
   */
  FMBCollectionFilter.prototype._getOriginalFacetCount = function (facetKey, value) {
    var facetValues = this.facets[facetKey];
    if (!facetValues) return 0;

    for (var i = 0; i < facetValues.length; i++) {
      if (String(facetValues[i].value) === String(value)) {
        return facetValues[i].count;
      }
    }

    return 0;
  };

  /**
   * Counts visible products matching a specific facet value.
   * @param {string} facetKey - Facet key
   * @param {string} value - Facet value to count
   * @returns {number}
   * @private
   */
  FMBCollectionFilter.prototype._countMatchesForValue = function (facetKey, value) {
    var mapping = FACET_FIELD_MAP[facetKey];
    if (!mapping) return 0;

    var count = 0;

    for (var i = 0; i < this.products.length; i++) {
      var product = this.products[i];
      if (!product.visible) continue;
      if (!product.fmb) continue;

      var productValue = product.fmb[mapping.prop];
      if (this._valueMatchesProduct(productValue, value, mapping.isArray)) count++;
    }

    return count;
  };

  /**
   * Checks if a product's field contains a given value.
   * @param {*} productValue - Product field value (scalar or array)
   * @param {string} targetValue - Value to check for
   * @param {boolean} isArray - Whether the field is an array
   * @returns {boolean}
   * @private
   */
  FMBCollectionFilter.prototype._valueMatchesProduct = function (productValue, targetValue, isArray) {
    if (productValue === undefined || productValue === null) return false;

    if (isArray) {
      if (!Array.isArray(productValue)) return false;
      return productValue.indexOf(targetValue) !== -1 ||
        productValue.indexOf(Number(targetValue)) !== -1;
    }

    return String(productValue) === targetValue;
  };

  /**
   * Shows or hides the no-results message.
   * @param {boolean} show - Whether to show the message
   * @private
   */
  FMBCollectionFilter.prototype._toggleNoResults = function (show) {
    var el = document.querySelector(SELECTORS.NO_RESULTS);
    if (!el) return;

    if (show) {
      el.removeAttribute('hidden');
    } else {
      el.setAttribute('hidden', '');
    }
  };

  /* -- updateActiveFilters ------------------------------------------------ */

  /** Renders active filter pills and updates the mobile count bubble. */
  FMBCollectionFilter.prototype.updateActiveFilters = function () {
    var containers = document.querySelectorAll(SELECTORS.ACTIVE_FILTERS);
    var html = this._buildActiveFilterPillsHtml();
    var hasActive = this._hasActiveFilters();

    for (var i = 0; i < containers.length; i++) {
      containers[i].innerHTML = html;
      if (hasActive) {
        containers[i].removeAttribute('hidden');
      } else {
        containers[i].setAttribute('hidden', '');
      }
    }

    this._updateClearAllVisibility(hasActive);
    this._updateMobileCountBubble();
  };

  /**
   * Builds HTML for all active filter pills.
   * @returns {string} HTML string
   * @private
   */
  FMBCollectionFilter.prototype._buildActiveFilterPillsHtml = function () {
    var pills = [];
    var facetKeys = Object.keys(this.activeFilters);

    for (var i = 0; i < facetKeys.length; i++) {
      var key = facetKeys[i];
      var label = FACET_LABELS[key] || key;
      var values = this.activeFilters[key];

      for (var j = 0; j < values.length; j++) {
        pills.push(this._buildSinglePillHtml(key, label, values[j]));
      }
    }

    return pills.join('');
  };

  /**
   * Builds HTML for a single active filter pill.
   * @param {string} facetKey - Facet key
   * @param {string} label - Human-readable label
   * @param {string} value - Filter value
   * @returns {string} HTML string
   * @private
   */
  FMBCollectionFilter.prototype._buildSinglePillHtml = function (facetKey, label, value) {
    return (
      '<span class="' + CLASSES.ACTIVE_FILTER + '"' +
        ' data-facet="' + escapeHtml(facetKey) + '"' +
        ' data-value="' + escapeHtml(value) + '">' +
        escapeHtml(label) + ': ' + escapeHtml(formatFacetValue(value)) +
        ' <button class="' + CLASSES.ACTIVE_CLOSE + '" aria-label="Remove filter" type="button">&times;</button>' +
      '</span>'
    );
  };

  /**
   * Shows or hides clear-all buttons based on whether filters are active.
   * @param {boolean} hasActive - Whether any filters are active
   * @private
   */
  FMBCollectionFilter.prototype._updateClearAllVisibility = function (hasActive) {
    var clearBtns = document.querySelectorAll(SELECTORS.CLEAR_ALL);
    for (var i = 0; i < clearBtns.length; i++) {
      if (hasActive) {
        clearBtns[i].removeAttribute('hidden');
      } else {
        clearBtns[i].setAttribute('hidden', '');
      }
    }
  };

  /**
   * Updates the mobile active filter count bubble.
   * @private
   */
  FMBCollectionFilter.prototype._updateMobileCountBubble = function () {
    var bubble = document.querySelector(SELECTORS.MOBILE_COUNT);
    if (!bubble) return;

    var total = this._getTotalActiveFilterCount();

    if (total > 0) {
      bubble.textContent = total;
      bubble.removeAttribute('hidden');
    } else {
      bubble.setAttribute('hidden', '');
    }
  };

  /**
   * Returns the total number of individually selected filter values.
   * @returns {number}
   * @private
   */
  FMBCollectionFilter.prototype._getTotalActiveFilterCount = function () {
    var total = 0;
    var keys = Object.keys(this.activeFilters);
    for (var i = 0; i < keys.length; i++) {
      total += this.activeFilters[keys[i]].length;
    }
    return total;
  };

  /* -- updateURL ---------------------------------------------------------- */

  /** Pushes the current filter state to the browser URL. */
  FMBCollectionFilter.prototype.updateURL = function () {
    var params = this._buildURLParams();
    var newUrl = window.location.pathname;

    if (params) newUrl += '?' + params;

    history.pushState({ fmbFilters: this.activeFilters }, '', newUrl);
  };

  /**
   * Builds URL search params string from active filters and current page.
   * @returns {string} URL params string (without leading ?)
   * @private
   */
  FMBCollectionFilter.prototype._buildURLParams = function () {
    var parts = [];
    var keys = Object.keys(this.activeFilters);

    for (var i = 0; i < keys.length; i++) {
      var paramKey = URL_PREFIX + keys[i];
      var paramValue = this.activeFilters[keys[i]].join(',');
      parts.push(encodeURIComponent(paramKey) + '=' + encodeURIComponent(paramValue));
    }

    return parts.join('&');
  };

  /* -- loadURLState ------------------------------------------------------- */

  /** Restores filter state from URL parameters on page load. */
  FMBCollectionFilter.prototype.loadURLState = function () {
    var params = new URLSearchParams(window.location.search);
    var hasState = false;

    params.forEach(function (value, key) {
      if (key.indexOf(URL_PREFIX) !== 0) return;

      var facetKey = key.substring(URL_PREFIX.length);
      if (!FACET_LABELS[facetKey]) return;

      var values = value.split(',');
      hasState = true;

      this._checkFilterValues(facetKey, values);
    }.bind(this));

    if (hasState) this.applyFilters();
  };

  /**
   * Checks checkboxes matching the given facet key and values.
   * @param {string} facetKey - Facet key
   * @param {Array} values - Array of values to check
   * @private
   */
  FMBCollectionFilter.prototype._checkFilterValues = function (facetKey, values) {
    var checkboxes = this.sidebar.querySelectorAll(
      'input[type="checkbox"][data-facet="' + facetKey + '"]'
    );

    for (var i = 0; i < checkboxes.length; i++) {
      if (values.indexOf(checkboxes[i].value) !== -1) {
        checkboxes[i].checked = true;
      }
    }
  };

  /* -- enhanceCards -------------------------------------------------------- */

  /** Adds stock badges showing block count and sqm to product cards. */
  FMBCollectionFilter.prototype.enhanceCards = function () {
    for (var i = 0; i < this.products.length; i++) {
      this._addStockBadge(this.products[i]);
    }
  };

  /**
   * Adds a stock badge to a single product card if it has FMB data.
   * @param {Object} product - Product object from this.products
   * @private
   */
  FMBCollectionFilter.prototype._addStockBadge = function (product) {
    if (!product.fmb || !product.fmb.block_count) return;

    var badgeContainer = product.element.querySelector(SELECTORS.CARD_BADGE);
    if (!badgeContainer) return;

    var sqm = product.fmb.total_available_sqm;
    var sqmText = sqm ? ' \u00B7 ' + Math.round(sqm) + 'm\u00B2' : '';

    var badge = document.createElement('span');
    badge.className = CLASSES.STOCK_BADGE;
    badge.textContent = product.fmb.block_count + ' blocks' + sqmText;

    badgeContainer.appendChild(badge);
  };

  /* -- bindEvents --------------------------------------------------------- */

  /** Sets up all event listeners using event delegation. */
  FMBCollectionFilter.prototype.bindEvents = function () {
    this._bindSidebarEvents();
    this._bindClearAllEvents();
    this._bindMobileEvents();
    this._bindPopState();
    this._bindPaginationEvents();
  };

  /**
   * Delegates checkbox and active-filter-pill events on the sidebar.
   * @private
   */
  FMBCollectionFilter.prototype._bindSidebarEvents = function () {
    var self = this;

    this.sidebar.addEventListener('change', function (e) {
      if (e.target.type === 'checkbox' && e.target.getAttribute('data-facet')) {
        self.applyFilters();
      }
    });

    this.sidebar.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('.' + CLASSES.ACTIVE_CLOSE);
      if (closeBtn) {
        self._handlePillRemove(closeBtn);
      }
    });
  };

  /**
   * Binds click handlers to all clear-all buttons.
   * @private
   */
  FMBCollectionFilter.prototype._bindClearAllEvents = function () {
    var self = this;

    document.addEventListener('click', function (e) {
      var clearBtn = e.target.closest(SELECTORS.CLEAR_ALL);
      if (!clearBtn) return;

      e.preventDefault();
      self._clearAllFilters();
    });
  };

  /**
   * Binds mobile drawer open/close/apply events.
   * @private
   */
  FMBCollectionFilter.prototype._bindMobileEvents = function () {
    var self = this;
    var trigger = document.querySelector(SELECTORS.MOBILE_TRIGGER);
    var closeBtn = document.querySelector(SELECTORS.MOBILE_CLOSE);
    var applyBtn = document.querySelector(SELECTORS.MOBILE_APPLY);
    var drawer = document.querySelector(SELECTORS.MOBILE_DRAWER);

    if (trigger) {
      trigger.addEventListener('click', function () {
        self._openMobileDrawer();
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        self._closeMobileDrawer();
      });
    }

    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        self._closeMobileDrawer();
      });
    }

    if (drawer) {
      drawer.addEventListener('click', function (e) {
        if (e.target === drawer) self._closeMobileDrawer();
      });
    }
  };

  /**
   * Listens for browser back/forward to restore filter state.
   * @private
   */
  FMBCollectionFilter.prototype._bindPopState = function () {
    var self = this;

    window.addEventListener('popstate', function () {
      self._uncheckAllCheckboxes();
      self.activeFilters = {}; /* clear stale state before reading URL */
      self.loadURLState();

      if (!self._hasActiveFilters()) {
        self._showAllProducts();
        self.updateCounts();
        self.updateActiveFilters();
      }
    });
  };

  /**
   * Delegates click events on the virtual pagination bar.
   * @private
   */
  FMBCollectionFilter.prototype._bindPaginationEvents = function () {
    var self = this;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-fmb-page]');
      if (!btn || btn.disabled || btn.hasAttribute('disabled')) return;

      var dir = btn.getAttribute('data-fmb-page');
      var totalPages = Math.ceil(self.filteredProducts.length / PAGE_SIZE);

      if (dir === 'prev' && self.currentPage > 1) {
        self.currentPage--;
        self._renderCurrentPage();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else if (dir === 'next' && self.currentPage < totalPages) {
        self.currentPage++;
        self._renderCurrentPage();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    });
  };

  /**
   * Handles removing a single filter pill by unchecking its checkbox.
   * @param {HTMLElement} closeBtn - The close button inside the pill
   * @private
   */
  FMBCollectionFilter.prototype._handlePillRemove = function (closeBtn) {
    var pill = closeBtn.closest('.' + CLASSES.ACTIVE_FILTER);
    if (!pill) return;

    var facetKey = pill.getAttribute('data-facet');
    var value = pill.getAttribute('data-value');

    this._uncheckSpecificFilter(facetKey, value);
    this.applyFilters();
  };

  /**
   * Unchecks a specific checkbox by facet key and value.
   * @param {string} facetKey - Facet key
   * @param {string} value - Checkbox value
   * @private
   */
  FMBCollectionFilter.prototype._uncheckSpecificFilter = function (facetKey, value) {
    var checkboxes = this.sidebar.querySelectorAll(
      'input[type="checkbox"][data-facet="' + facetKey + '"]'
    );

    for (var i = 0; i < checkboxes.length; i++) {
      if (checkboxes[i].value === value) {
        checkboxes[i].checked = false;
      }
    }
  };

  /**
   * Clears all filters: unchecks checkboxes and reapplies.
   * @private
   */
  FMBCollectionFilter.prototype._clearAllFilters = function () {
    this._uncheckAllCheckboxes();
    this.applyFilters();
  };

  /**
   * Unchecks every checkbox in the sidebar.
   * @private
   */
  FMBCollectionFilter.prototype._uncheckAllCheckboxes = function () {
    var checkboxes = this.sidebar.querySelectorAll('input[type="checkbox"]:checked');
    for (var i = 0; i < checkboxes.length; i++) {
      checkboxes[i].checked = false;
    }
  };

  /**
   * Resets to show all products (no filters) and re-renders page 1.
   * @private
   */
  FMBCollectionFilter.prototype._showAllProducts = function () {
    for (var i = 0; i < this.products.length; i++) {
      this.products[i].visible = true;
    }
    this.filteredProducts = this.products.slice();
    this.currentPage = 1;
    this._renderCurrentPage();
  };

  /* -- Mobile Drawer ------------------------------------------------------ */

  /**
   * Opens the mobile filter drawer.
   * @private
   */
  FMBCollectionFilter.prototype._openMobileDrawer = function () {
    var drawer = document.querySelector(SELECTORS.MOBILE_DRAWER);
    if (!drawer) return;

    drawer.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  };

  /**
   * Closes the mobile filter drawer.
   * @private
   */
  FMBCollectionFilter.prototype._closeMobileDrawer = function () {
    var drawer = document.querySelector(SELECTORS.MOBILE_DRAWER);
    if (!drawer) return;

    drawer.setAttribute('hidden', '');
    document.body.style.overflow = '';
  };

  /* -----------------------------------------------------------------------
   * Initialization
   * ----------------------------------------------------------------------- */

  function boot() {
    var filter = new FMBCollectionFilter();
    filter.init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
